import React, { useState } from 'react';
import PropTypes from 'prop-types';
import useListenSize from '../../hooks/useListenSize';

/**
 * A card with a header (icon, title, subtitle, total, chevron) and a
 * toggle-able body. Used for the Due Today and Recurring Payments cards,
 * and as the shell for OwnershipTimeline.
 */
export function CollapsibleLineItems({ icon, title, subtitle, total, defaultOpen, className, children }) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  useListenSize(isOpen);

  const rootClassName = ['k-collapsible-card', className].filter(Boolean).join(' ');
  const chevronClassName = ['k-collapsible-card__chevron', isOpen && 'k-collapsible-card__chevron--open']
    .filter(Boolean)
    .join(' ');

  return (
    <section className={rootClassName}>
      <button
        type="button"
        className="k-collapsible-card__header"
        onClick={() => setIsOpen(prev => !prev)}
        aria-expanded={isOpen}
        aria-label={title}
      >
        {icon && <span className="k-collapsible-card__icon">{icon}</span>}
        <span className="k-collapsible-card__heading">
          <span className="k-collapsible-card__title">{title}</span>
          {subtitle && <span className="k-collapsible-card__subtitle">{subtitle}</span>}
        </span>
        {total !== undefined && <span className="k-collapsible-card__total">{total}</span>}
        <span className={chevronClassName} aria-hidden="true" />
      </button>
      {isOpen && <div className="k-collapsible-card__body">{children}</div>}
    </section>
  );
}

CollapsibleLineItems.propTypes = {
  icon: PropTypes.node,
  title: PropTypes.node.isRequired,
  subtitle: PropTypes.node,
  total: PropTypes.node,
  defaultOpen: PropTypes.bool,
  className: PropTypes.string,
  children: PropTypes.node,
};

CollapsibleLineItems.defaultProps = {
  icon: null,
  subtitle: null,
  total: undefined,
  defaultOpen: true,
  className: '',
  children: null,
};

export function LineItemRow({ label, value, action }) {
  return (
    <div className="k-collapsible-card__row">
      <span className="k-collapsible-card__row-label-group">
        <span className="k-collapsible-card__row-label">{label}</span>
        {action && <span className="k-collapsible-card__row-action">{action}</span>}
      </span>
      <span className="k-collapsible-card__row-value">{value}</span>
    </div>
  );
}

LineItemRow.propTypes = {
  label: PropTypes.node.isRequired,
  value: PropTypes.node.isRequired,
  action: PropTypes.node,
};

LineItemRow.defaultProps = {
  action: null,
};
